import van, { type ChildDom } from "vanjs-core"

/**
 * Simple hash-based router for Van.js SPA
 * Routes use hash format: #/path/to/page
 */

// =============================================================================
// Types
// =============================================================================

export interface Route {
	path: string
	label?: string
	component: () => HTMLElement
}

export interface RouteMatch {
	route: Route | null
	params: Record<string, string>
}

// =============================================================================
// State
// =============================================================================

export const current_path = van.state("/")
export const route_params = van.state<Record<string, string>>({})

// =============================================================================
// Router Functions
// =============================================================================

/**
 * Navigate to a path
 */
export function navigate(path: string) {
	window.location.hash = path
}

/**
 * Navigate back using browser history, falling back to a given path
 * when there is no prior in-app history (e.g. opened via direct link).
 */
export function go_back(fallback: string = "/") {
	if (window.history.length > 1) {
		window.history.back()
	} else {
		navigate(fallback)
	}
}

/**
 * Parse route params from path pattern
 * e.g., /article/:slug with /article/hello-world → { slug: 'hello-world' }
 */
function match_route(pattern: string, path: string): Record<string, string> | null {
	const pattern_parts = pattern.split("/").filter(Boolean)
	const path_parts = path.split("/").filter(Boolean)

	if (pattern_parts.length !== path_parts.length) {
		// Check if pattern ends with wildcard
		if (!pattern.endsWith("/*")) {
			return null
		}
		// For wildcard, just need path to start with pattern prefix
		const prefix_parts = pattern_parts.slice(0, -1)
		if (path_parts.length < prefix_parts.length) {
			return null
		}
		for (let i = 0; i < prefix_parts.length; i++) {
			if (!prefix_parts[i].startsWith(":") && prefix_parts[i] !== path_parts[i]) {
				return null
			}
		}
	}

	const params: Record<string, string> = {}

	for (let i = 0; i < pattern_parts.length; i++) {
		const pattern_part = pattern_parts[i]
		const path_part = path_parts[i]

		if (pattern_part === "*") {
			params["*"] = path_parts.slice(i).join("/")
			break
		}

		if (pattern_part.startsWith(":")) {
			const param_name = pattern_part.slice(1)
			params[param_name] = path_part
		} else if (pattern_part !== path_part) {
			return null
		}
	}

	return params
}

/**
 * Find matching route from routes array
 */
export function find_route(routes: Route[], path: string): RouteMatch {
	for (const route of routes) {
		const params = match_route(route.path, path)
		if (params !== null) {
			return { route, params }
		}
	}
	return { route: null, params: {} }
}

/** Route change listener */
type OnRouteChange = (path: string) => void
let route_listener: OnRouteChange | null = null
let last_dispatched_hash: string | null = null

function current_hash(): string {
	return window.location.hash.slice(1) || "/"
}

function current_hash_path(): string {
	return current_hash().split("?")[0]
}

function dispatch_route(path: string) {
	// Dedupe on the FULL hash (path + query) — not just path. This lets
	// `/chat?gate=A` → `/chat?gate=B` re-mount (same path, different query)
	// while still suppressing duplicate dispatches during init.
	const hash = current_hash()
	if (hash === last_dispatched_hash) {
		return
	}
	last_dispatched_hash = hash
	if (route_listener) {
		route_listener(path)
	}
}

/**
 * Register a callback for route changes. Fires immediately with the current
 * path, then on every distinct hash change.
 */
export function on_route_change(fn: OnRouteChange) {
	route_listener = fn
	dispatch_route(current_hash_path())
}

/**
 * Initialize the router - call once on app start.
 */
export function init_router() {
	function handle_hash_change() {
		const path = current_hash_path()
		current_path.val = path
		dispatch_route(path)
	}

	window.addEventListener("hashchange", handle_hash_change)
	// Set initial path state (for sidebar reactivity etc.)
	current_path.val = current_hash_path()
}

/**
 * Resolve a path to a route element. Pure function, no reactive state reads.
 */
export function resolve_route(
	routes: Route[],
	path: string,
	not_found: () => HTMLElement,
): HTMLElement {
	const { route, params } = find_route(routes, path)
	route_params.val = params

	if (route) {
		return route.component()
	}
	return not_found()
}

/**
 * Get query params from hash-based URL
 * For URL like #/path?foo=bar, returns URLSearchParams with foo=bar
 */
export function get_hash_query_params(): URLSearchParams {
	const hash = window.location.hash
	const query_index = hash.indexOf("?")
	const query_string = query_index >= 0 ? hash.slice(query_index + 1) : ""
	return new URLSearchParams(query_string)
}

/**
 * Link component for navigation
 */
export function Link(props: { href: string; class?: string; children: ChildDom }) {
	const t = van.tags
	return t.a(
		{
			href: `#${props.href}`,
			class: props.class || "",
		},
		props.children,
	)
}

// =============================================================================
// Lazy Loading
// =============================================================================

type LazyImportFn = () => Promise<{ default: () => HTMLElement }>

/**
 * Lazy load a route component using dynamic import
 * Shows a loading spinner while the component loads
 *
 * @example
 * const routes = [
 *   { path: '/help', component: lazy(() => import('./help_center').then(m => ({ default: m.HelpHome }))) },
 * ]
 */
export function lazy(import_fn: LazyImportFn): () => HTMLElement {
	let cached: (() => HTMLElement) | null = null

	return () => {
		// Return cached component if already loaded
		if (cached) {
			return cached()
		}

		const t = van.tags

		// Create loading placeholder
		const container = t.div(
			{
				class: "flex justify-center items-center min-h-[200px] text-slate-400 dark:text-slate-500",
			},
			t.span({ class: "animate-pulse" }, "Se încarcă…"),
		)

		// Load component asynchronously
		import_fn()
			.then((module) => {
				cached = module.default
				// Replace loading with actual component
				const content = cached()
				container.replaceWith(content)
			})
			.catch((err) => {
				console.error("Failed to load route component:", err)
				container.innerHTML = ""
				van.add(
					container,
					t.div(
						{ class: "p-6 text-red-600 dark:text-red-400" },
						t.span("Pagina nu s-a putut încărca. Reîmprospătați."),
					),
				)
			})

		return container
	}
}

/**
 * Preload a lazy component (e.g., on link hover)
 * Useful for improving perceived performance
 *
 * @example
 * t.a({
 *   href: '#/help',
 *   onmouseenter: () => preload(() => import('./help_center'))
 * }, 'Help')
 */
export function preload(import_fn: () => Promise<unknown>): void {
	import_fn().catch(() => {}) // Silently preload
}
